import type { CSSProperties, ReactNode } from 'react'

/**
 * CommandCenterShell — the shared Square Code V2 command-deck chrome.
 *
 * PHASE_2G: one component for the header/navigation/layout every V2 surface was
 * duplicating (Q home, ← Command Center, optional parent back, breadcrumb,
 * title + status, honesty badge, optional top nav bar, optional context bar,
 * wide content frame, optional right rail + footer). Follows the Law Pocket
 * project-command pattern and the #97/#99 command-deck look — it standardizes
 * structure, it does NOT introduce a new design language.
 *
 * Presentational + SSR-safe: callers pass navigation callbacks and content
 * slots. The shell never fakes data and imposes no business logic.
 */

export const CC_INK = { primary: '#c0c8d2', secondary: '#8a94a0', muted: '#828c99', faint: '#78818e', whisper: '#3a4250' }
export const CC_MONO = "'SF Mono', 'JetBrains Mono', ui-monospace, monospace"
export const CC_FONT = "'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif"

export interface CommandNavItem {
  id: string
  label: string
  accent?: string
}

export interface CommandCenterShellProps {
  /**
   * Q home — ALWAYS the main Command Center (/square/command-center).
   * PHASE_2M hard rule: the Q icon takes the operator back to the Command
   * Center home from every nested view. The legacy Square dashboard stays
   * reachable via the sidebar, never via Q.
   */
  onHome?: () => void
  /** ← Command Center (/square/command-center). */
  onBackCommandCenter?: () => void
  /** Optional parent back control (e.g. ← Operations). */
  parentBack?: { label: string; onClick?: () => void }
  /** Breadcrumb segments, joined by `breadcrumbSeparator`. Rendered as one node. */
  breadcrumb: string[]
  /** Separator between breadcrumb segments. Defaults to '/'; the division layer passes '·'. */
  breadcrumbSeparator?: string
  title: string
  /** Accent for the title text + leading dot. Defaults to the neutral ink. */
  titleAccent?: string
  /** Show the colored leading dot before the title. Defaults to true. */
  titleDot?: boolean
  /** Optional status badge rendered inline after the title (e.g. module status). */
  statusBadge?: ReactNode
  /** The honest "... NOT FAKED" badge, top-right. label + hover title. */
  honestyBadge?: { label: string; title: string }
  /** Optional top nav / section bar (e.g. the division switcher). */
  topNav?: { items: CommandNavItem[]; activeId?: string; onSelect?: (id: string) => void }
  /** Optional context / module bar rendered under the header (e.g. purpose + quick-add). */
  contextBar?: ReactNode
  /** Main content slot (wrapped in the wide frame). */
  children: ReactNode
  /** Optional right rail; when present the main area becomes a two-column grid. */
  rightRail?: ReactNode
  /** Optional footer / receipt slot under the main frame. */
  footer?: ReactNode
  /** Wide content frame max width. Defaults to 1680. */
  maxWidth?: number
}

const navBtn: CSSProperties = {
  padding: '6px 12px', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)', color: CC_INK.secondary, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
}

function QHome({ onHome }: { onHome?: () => void }) {
  return (
    <button
      type="button"
      onClick={onHome}
      title="Home — back to the Command Center"
      aria-label="Home — Command Center"
      style={{
        width: 34, height: 34, flexShrink: 0, borderRadius: 9, cursor: 'pointer',
        background: 'linear-gradient(135deg,#1a1028,#0d0a14)', border: '1px solid rgba(124,58,237,0.3)',
        color: '#7c3aed', fontWeight: 900, fontSize: 16, textShadow: '0 0 8px rgba(124,58,237,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      Q
    </button>
  )
}

export default function CommandCenterShell({
  onHome, onBackCommandCenter, parentBack, breadcrumb, breadcrumbSeparator = '/',
  title, titleAccent, titleDot = true, statusBadge, honestyBadge, topNav, contextBar,
  children, rightRail, footer, maxWidth = 1680,
}: CommandCenterShellProps) {
  const accent = titleAccent || CC_INK.primary
  return (
    <div style={{ width: '100%', height: '100vh', overflow: 'hidden', background: '#0a0b10', color: CC_INK.primary, fontFamily: CC_FONT, display: 'flex', flexDirection: 'column' }}>
      {/* Stable header — Q home, back controls, breadcrumb, title, honesty badge. */}
      <header style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 32px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <QHome onHome={onHome} />
        <button type="button" onClick={onBackCommandCenter} style={navBtn}>← Command Center</button>
        {parentBack && <button type="button" onClick={parentBack.onClick} style={navBtn}>{parentBack.label}</button>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, letterSpacing: '0.2em', color: CC_INK.faint, fontWeight: 800, fontFamily: CC_MONO }}>
            {breadcrumb.join(` ${breadcrumbSeparator} `)}
          </div>
          <h1 style={{ margin: '2px 0 0', display: 'flex', alignItems: 'center', gap: 10, fontSize: 22, fontWeight: 900, color: accent, letterSpacing: '-0.01em' }}>
            {titleDot && <span style={{ width: 9, height: 9, borderRadius: '50%', background: accent, display: 'inline-block' }} />}
            {title}
            {statusBadge}
          </h1>
        </div>
        {honestyBadge && (
          <span
            title={honestyBadge.title}
            style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', fontFamily: CC_MONO, color: '#b8862a', padding: '4px 9px', borderRadius: 6, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.22)', cursor: 'help', whiteSpace: 'nowrap' }}
          >
            {honestyBadge.label}
          </span>
        )}
      </header>

      {/* Optional top nav / section bar — switch the active section in a stable shell. */}
      {topNav && (
        <nav style={{ flexShrink: 0, display: 'flex', gap: 3, padding: '8px 32px', overflowX: 'auto', borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.012)' }}>
          {topNav.items.map((it) => {
            const on = topNav.activeId === it.id
            const a = it.accent || CC_INK.secondary
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => topNav.onSelect?.(it.id)}
                style={{
                  padding: '6px 14px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap', fontSize: 12,
                  fontWeight: on ? 800 : 600, fontFamily: 'inherit', color: on ? a : CC_INK.muted,
                  background: on ? a + '18' : 'transparent', border: '1px solid ' + (on ? a + '33' : 'transparent'),
                  transition: 'all 0.15s ease',
                }}
              >
                {it.label}
              </button>
            )
          })}
        </nav>
      )}

      {/* Optional context / module bar (purpose + quick-add, etc.). */}
      {contextBar && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14, padding: '10px 32px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.012)' }}>
          {contextBar}
        </div>
      )}

      <main style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: rightRail ? '22px 32px 60px' : '24px 32px 60px' }}>
        <div style={{ maxWidth, margin: '0 auto', ...(rightRail ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1.7fr) minmax(300px, 1fr)', gap: 18, alignItems: 'start' } : {}) }}>
          {children}
          {rightRail}
        </div>
        {footer && <div style={{ maxWidth, margin: '18px auto 0' }}>{footer}</div>}
      </main>
    </div>
  )
}
